/**
 * Temas do cliente.
 *
 * Toda a folha de estilo assenta em custom properties do `:root`, por isso
 * trocar de tema é só duas coisas:
 *   (a) pôr `data-theme` no `<html>` — as SUPERFÍCIES (fundos, texto, linhas e,
 *       nos temas "vibe", uma CENA de fundo `--app-bg`) vêm de blocos
 *       `[data-theme="…"]` no CSS, que o cascade troca de imediato sem
 *       reescrever regra nenhuma;
 *   (b) sobrepor as variáveis de ACENTO inline no `<html>`, porque o acento é
 *       uma cor livre (qualquer hex) que o CSS não consegue enumerar à partida.
 *
 * Cada tema pode trazer o SEU acento de assinatura (`accent`): escolher
 * "Synthwave" não deve deixar o botão verde de meia-noite — a vibe é o conjunto
 * fundo+superfícies+acento. O utilizador continua livre de trocar o acento a
 * seguir; a escolha manual passa a ser a que persiste.
 *
 * A fonte de verdade é o DOM + `localStorage` — não o store, de propósito: o
 * tema é do DISPOSITIVO, tem de valer antes de haver sessão e sobreviver ao
 * logout. Aplica-se no arranque (`main.tsx`) ANTES do primeiro render, senão
 * pisca a paleta por omissão até o React montar.
 */

export type ThemeGroup = "Escuro" | "Claro" | "Vibe" | "Cena";

export interface ThemeOption {
  id: string;
  name: string;
  group: ThemeGroup;
  /** Amostras para o cartão de pré-visualização: [fundo, superfície, texto]. */
  swatch: [string, string, string];
  /**
   * Acento de assinatura em `#rrggbb`. Aplicado quando o tema é escolhido — as
   * "vibes" só ficam coesas se o botão também mudar de cor. Ausente = mantém o
   * acento actual (os temas neutros midnight/abyss/… não impõem cor).
   */
  accent?: string;
  /**
   * Grupo **Cena**: caminho de uma FOTOGRAFIA real usada como plano de fundo
   * (servida de `apps/web/public/scenes/`). O CSS do `[data-theme]` respectivo
   * põe esta imagem em `--app-bg` por baixo de um degradê de gradação na cor da
   * vibe (legibilidade + coesão), e os painéis translúcidos deixam-na
   * transparecer através de toda a UI. Quando presente, o cartão de
   * pré-visualização mostra a própria foto em vez das amostras de cor.
   */
  image?: string;
}

/**
 * `midnight` é a base (o `:root`). Os outros são blocos `[data-theme]` no CSS —
 * manter os ids em sincronia com os selectores da folha de estilo.
 *
 * Grupos: **Escuro** (neutros), **Claro**, **Vibe** (fundo cénico + acento
 * próprio — floresta, synthwave, oceano, …).
 */
export const THEMES: readonly ThemeOption[] = [
  // ── neutros escuros ─────────────────────────────────────────
  { id: "midnight", name: "Meia-noite", group: "Escuro", swatch: ["#06090d", "#18222d", "#e6edf3"] },
  { id: "abyss", name: "Abismo · OLED", group: "Escuro", swatch: ["#000000", "#141418", "#e8e8ea"] },
  { id: "slate", name: "Ardósia", group: "Escuro", swatch: ["#15181d", "#2e343d", "#e6edf3"] },
  { id: "nord", name: "Nórdico", group: "Escuro", swatch: ["#2e3440", "#434c5e", "#eceff4"] },
  // ── claros ──────────────────────────────────────────────────
  { id: "light", name: "Claro", group: "Claro", swatch: ["#e7ebf0", "#ffffff", "#141a22"] },
  {
    id: "sakura",
    name: "Sakura",
    group: "Claro",
    swatch: ["#ffe4ec", "#fff7fa", "#4a2333"],
    accent: "#e84a90",
  },
  // ── vibes (fundo cénico + acento de assinatura) ─────────────
  {
    id: "forest",
    name: "Floresta",
    group: "Vibe",
    swatch: ["#0a1712", "#122a1f", "#e4f0e8"],
    accent: "#4ade80",
  },
  {
    id: "synthwave",
    name: "Synthwave",
    group: "Vibe",
    swatch: ["#180a29", "#231041", "#f6e9ff"],
    accent: "#f72585",
  },
  {
    id: "tokyo",
    name: "Tokyo Night",
    group: "Vibe",
    swatch: ["#10131f", "#1c2033", "#c0caf5"],
    accent: "#7aa2f7",
  },
  {
    id: "sunset",
    name: "Pôr do Sol",
    group: "Vibe",
    swatch: ["#2a1531", "#361a30", "#fbe6dd"],
    accent: "#ff7a59",
  },
  {
    id: "ocean",
    name: "Oceano",
    group: "Vibe",
    swatch: ["#061a29", "#0e2c40", "#d8ecf5"],
    accent: "#22d3ee",
  },
  {
    id: "aurora",
    name: "Aurora",
    group: "Vibe",
    swatch: ["#05080f", "#141c31", "#dbe4f0"],
    accent: "#5eead4",
  },
  {
    id: "dracula",
    name: "Drácula",
    group: "Vibe",
    swatch: ["#21222c", "#2f313f", "#f8f8f2"],
    accent: "#bd93f9",
  },
  {
    id: "nebula",
    name: "Nébula",
    group: "Vibe",
    swatch: ["#0b0716", "#1e1235", "#ece7ff"],
    accent: "#c084fc",
  },
  // ── cenas (FOTOGRAFIA real de fundo + gradação na cor da vibe) ─────
  // A foto vive em `--app-bg` sob um degradê de gradação; os painéis
  // translúcidos com blur deixam-na transparecer por toda a UI. O `swatch`
  // fica como fallback do cartão caso a imagem não carregue.
  {
    id: "photo-aurora",
    name: "Aurora Boreal",
    group: "Cena",
    swatch: ["#0a0817", "#1a1330", "#ece7ff"],
    accent: "#a855f7",
    image: "/scenes/photo-aurora.webp",
  },
  {
    id: "photo-forest",
    name: "Floresta de Névoa",
    group: "Cena",
    swatch: ["#08130d", "#122a1f", "#e4f0e8"],
    accent: "#34d399",
    image: "/scenes/photo-forest.webp",
  },
  {
    id: "photo-ocean",
    name: "Oceano Profundo",
    group: "Cena",
    swatch: ["#04141f", "#0e2c40", "#d8ecf5"],
    accent: "#22d3ee",
    image: "/scenes/photo-ocean.webp",
  },
  {
    id: "photo-peaks",
    name: "Montanhas",
    group: "Cena",
    swatch: ["#0a111c", "#1a2740", "#dbe6f5"],
    accent: "#60a5fa",
    image: "/scenes/photo-peaks.webp",
  },
  {
    id: "photo-city",
    name: "Cidade Neon",
    group: "Cena",
    swatch: ["#120714", "#2a1030", "#ffe6f5"],
    accent: "#f472b6",
    image: "/scenes/photo-city.webp",
  },
  {
    id: "photo-dunes",
    name: "Dunas ao Pôr do Sol",
    group: "Cena",
    swatch: ["#1f1206", "#3a2412", "#ffeeda"],
    accent: "#fb923c",
    image: "/scenes/photo-dunes.webp",
  },
];

export interface AccentOption {
  id: string;
  name: string;
  hex: string;
}

export const ACCENTS: readonly AccentOption[] = [
  { id: "green", name: "Verde", hex: "#22c55e" },
  { id: "blue", name: "Azul", hex: "#3b82f6" },
  { id: "indigo", name: "Índigo", hex: "#6366f1" },
  { id: "purple", name: "Roxo", hex: "#8b5cf6" },
  { id: "pink", name: "Rosa", hex: "#ec4899" },
  { id: "red", name: "Vermelho", hex: "#ef4444" },
  { id: "amber", name: "Âmbar", hex: "#f59e0b" },
  { id: "teal", name: "Turquesa", hex: "#14b8a6" },
];

export interface ThemeState {
  /** Um dos ids de `THEMES`. */
  theme: string;
  /** Cor de acento em `#rrggbb`. */
  accent: string;
}

const THEME_KEY = "gv:theme";
const ACCENT_KEY = "gv:accent";
/**
 * O tema com que se entra à primeira vez.
 *
 * Era `midnight` — o `:root`, um azul quase neutro que se lia como cinzento
 * lavado. É a Green Village: o verde já está nos botões, nos estados ligados e
 * no logótipo, e ficava sozinho sobre um fundo que não era de lado nenhum.
 *
 * Não força nada a ninguém: isto é só o ponto de PARTIDA. Quem já escolheu tema
 * tem a escolha guardada no seu navegador e não dá por esta linha (ver
 * `loadTheme`); e quem quiser o cinzento tem-no na lista, com o nome que sempre
 * teve.
 *
 * Passou do "Floresta" (vibe, tudo verde) para a cena "Floresta de Névoa", a
 * pedido: *"quero que inicie sempre com o tema Cena da floresta de névoa"*.
 */
export const DEFAULT_THEME = "photo-forest";
export const DEFAULT_ACCENT = "#22c55e";

/** O acento de assinatura de um tema, se tiver. */
export function themeAccent(themeId: string): string | undefined {
  return THEMES.find((t) => t.id === themeId)?.accent;
}

/* ── cor ──────────────────────────────────────────────────────── */

/** `#rgb` ou `#rrggbb` (com ou sem `#`) → [r,g,b] 0-255, ou null se inválido. */
function parseHex(hex: string): [number, number, number] | null {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return null;
  let body = match[1]!;
  if (body.length === 3) body = body[0]! + body[0]! + body[1]! + body[1]! + body[2]! + body[2]!;
  const n = parseInt(body, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex([r, g, b]: [number, number, number]): string {
  const channel = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v)))
      .toString(16)
      .padStart(2, "0");
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

/** Escurece por um factor 0-1 — dá o `--green-dark` do hover a partir do acento. */
function darken(rgb: [number, number, number], factor: number): [number, number, number] {
  return [rgb[0] * (1 - factor), rgb[1] * (1 - factor), rgb[2] * (1 - factor)];
}

export function isValidHex(hex: string): boolean {
  return parseHex(hex) !== null;
}

/** Normaliza para `#rrggbb`, ou null se inválido. Para o `<input type=color>`. */
export function normalizeHex(hex: string): string | null {
  const rgb = parseHex(hex);
  return rgb ? toHex(rgb) : null;
}

/**
 * As três variáveis de acento derivadas de um hex: a cor, uma versão escura
 * para hovers, e uma translúcida (alpha 26 ≈ 15%) para fundos suaves.
 */
export function accentVars(hex: string): Record<string, string> {
  const rgb = parseHex(hex) ?? parseHex(DEFAULT_ACCENT)!;
  const base = toHex(rgb);
  return {
    "--green": base,
    "--green-dark": toHex(darken(rgb, 0.18)),
    "--green-soft": `${base}26`,
  };
}

/* ── aplicação / persistência ─────────────────────────────────── */

export function applyTheme(state: ThemeState): void {
  const root = document.documentElement;
  root.dataset.theme = state.theme;
  for (const [key, value] of Object.entries(accentVars(state.accent))) {
    root.style.setProperty(key, value);
  }
}

function read(key: string, fallback: string): string {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    // Modo privado / storage bloqueado — o tema por omissão serve.
    return fallback;
  }
}

export function loadTheme(): ThemeState {
  const theme = read(THEME_KEY, DEFAULT_THEME);
  const accent = read(ACCENT_KEY, DEFAULT_ACCENT);
  return {
    theme: THEMES.some((t) => t.id === theme) ? theme : DEFAULT_THEME,
    accent: normalizeHex(accent) ?? DEFAULT_ACCENT,
  };
}

/** Guarda E aplica — a UI chama isto a cada mudança para a pré-visualização ser ao vivo. */
export function saveTheme(state: ThemeState): void {
  const normalized: ThemeState = {
    theme: THEMES.some((t) => t.id === state.theme) ? state.theme : DEFAULT_THEME,
    accent: normalizeHex(state.accent) ?? DEFAULT_ACCENT,
  };
  try {
    localStorage.setItem(THEME_KEY, normalized.theme);
    localStorage.setItem(ACCENT_KEY, normalized.accent);
  } catch {
    // Sem persistência possível — fica válido só nesta sessão.
  }
  applyTheme(normalized);
}
